import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import Slider from '@/components/Slider.tsx'
import VerticalScrollArea from '@/components/common/VerticalScrollArea.tsx'
import Icon, { type IconName } from '@/components/common/Icon.tsx'
import useSpeechDialogStore from './speech-dialog.store.ts'
import MediaSourceSelector from './MediaSourceSelector.tsx'
import { SpeechContentType } from './types.ts'
import VoiceBox from './VoiceBox.tsx'
import ExpressiveVoiceToggle from './ExpressiveVoiceToggle.tsx'

const TABS = ['text', 'speech'] as const
type TabOption = (typeof TABS)[number]

const Tab: FC<{
  label: string
  value: TabOption
  iconName: IconName
  active: boolean
  onClick: () => void
}> = ({ label, value, iconName, active, onClick }) => {
  const { t } = useTranslation()
  return (
    <button
      key={value}
      className={`flex h-[52px] flex-1 items-center justify-center gap-2 px-6 text-sm font-semibold uppercase focus:outline-none ${
        active
          ? 'border-b border-b-blue-500 text-white'
          : 'cursor-pointer border-b border-b-neutral-700 text-gray-300'
      }`}
      type="button"
      onClick={onClick}
    >
      <Icon
        name={iconName}
        width={20}
        height={20}
        fill={active ? '#ffffff' : '#d4d4d8'}
      />
      <span>{t(label)}</span>
    </button>
  )
}

const VoiceSettings: FC = () => {
  const { t } = useTranslation()
  const {
    expressive,
    speed,
    setExpressive,
    setSpeed,
    selectedVoice,
    setContentType,
    selectedTab,
    setSelectedTab,
    stopRecorderFn,
  } = useSpeechDialogStore()
  const isActive = (tab: TabOption) => selectedTab === tab
  const tabOptions = TABS.map((tab) => ({
    textKey: t(`ls_speech_dialog_${tab}_to_speech`),
    value: tab,
    iconName: tab,
  }))

  const handleSelectTab = (tab: TabOption) => {
    stopRecorderFn()
    setSelectedTab(tab)
  }

  return (
    <div className="flex max-h-[552px] min-w-fit max-w-[520px] flex-1 flex-col overflow-hidden">
      <div className="flex w-full">
        {tabOptions.map(({ textKey, value, iconName }) => (
          <Tab
            key={value}
            label={t(textKey)}
            value={value}
            iconName={iconName}
            active={isActive(value)}
            onClick={() => handleSelectTab(value)}
          />
        ))}
      </div>
      <VerticalScrollArea className="max-h-[500px]">
        <div className="flex flex-col gap-8 px-5 py-6">
          {isActive('speech') && <MediaSourceSelector />}
          <div className="flex flex-col gap-3">
            <span className="text-sm font-medium text-white">
              {t('ls_generic_output_voice')}
            </span>
            {selectedVoice && (
              <VoiceBox {...selectedVoice}>
                <button
                  type="button"
                  className="mt-[12px] flex w-full flex-row items-center justify-center rounded-[4px] bg-white/[6%] px-[16px] py-[8px] text-[14px] font-bold text-white hover:bg-white/[12%]"
                  onClick={() => {
                    setContentType(SpeechContentType.Voices)
                  }}
                >
                  {t('ls_generic_change_voice')}
                </button>
              </VoiceBox>
            )}
          </div>
          {!isActive('speech') && (
            <div className="flex flex-col gap-3">
              <span>{t('ls_generic_speed')}</span>
              <span className="inline-flex flex-row items-center gap-[10px]">
                <Slider
                  defaultValue={[speed]}
                  min={0.8}
                  max={1.2}
                  step={0.05}
                  markers={5}
                  onValueChange={([value]) => {
                    setSpeed(value ?? 1)
                  }}
                />
                <span className="min-w-[30px] text-right text-[12px] font-semibold">
                  {Math.round(speed * 100)}%
                </span>
              </span>
            </div>
          )}
          <div className="flex flex-col gap-3">
            <span>{t('ls_generic_voice_options')}</span>
            <ExpressiveVoiceToggle
              checked={expressive}
              onCheckedChange={setExpressive}
              tooltipPosition="top"
            />
          </div>
        </div>
      </VerticalScrollArea>
    </div>
  )
}

export default VoiceSettings
